import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlienIcon, CheckCircleIcon, PaletteIcon } from '@fronton/icons-react';
import {
  ButtonWithGAClickButton,
  GalleryModal,
  List,
  ListItem,
  Section,
  SectionSubTitle,
  Typography,
} from 'common/components';
import { analyticsUtils } from 'utils/analytics-utils';
import { CEILINGS_CDN_PATH, CEILINGS_ELEMENT_BLOCK } from '../../ceilings-constants';
import { getPhotoPrintingGalleryList } from './ceilings-photo-printing-section-constants';
import {
  CeilingsPhotoPrintingImage,
  ceilingsPhotoPrintingBigImage,
  ceilingsPhotoPrintingImageWrapper,
  ceilingsPhotoPrintingList,
  ceilingsPhotoPrintingSection,
  ceilingsPhotoPrintingText,
  ceilingsPhotoPrintingTextWrapper,
} from './ceilings-photo-printing-section-styles';

type CeilingsPhotoPrintingSectionProps = {
  onConsultationSignupBtnClick: () => void;
};

export const CeilingsPhotoPrintingSection = ({
  onConsultationSignupBtnClick,
}: CeilingsPhotoPrintingSectionProps) => {
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const galleryList = getPhotoPrintingGalleryList(t);

  const handleImageClick = useCallback(
    (index: number) => () => {
      setIsModalVisible(true);
      setActiveImageIndex(index);
      analyticsUtils.clickButtonEvent(
        CEILINGS_ELEMENT_BLOCK,
        galleryList[index].name,
        t('photo-printing-section.title')
      );
    },
    [galleryList, t]
  );

  const handleCloseModal = useCallback(() => {
    setIsModalVisible(false);
    setActiveImageIndex(0);
  }, []);

  const handleConsultationSignUpBtnClick = useCallback(() => {
    onConsultationSignupBtnClick();
    setIsModalVisible(false);
    setActiveImageIndex(0);
  }, [onConsultationSignupBtnClick]);

  return (
    <Section className={ceilingsPhotoPrintingSection}>
      <div className={ceilingsPhotoPrintingTextWrapper}>
        <SectionSubTitle margin="s">{t('photo-printing-section.title')}</SectionSubTitle>
        <Typography adaptive={false} className={ceilingsPhotoPrintingText} variant="m" size="body_long">
          {t('photo-printing-section.description')}
        </Typography>

        <List className={ceilingsPhotoPrintingList}>
          <ListItem leftIcon={<CheckCircleIcon />} text={t('photo-printing-section.benefits.first') || ''} />
          <ListItem leftIcon={<PaletteIcon />} text={t('photo-printing-section.benefits.second') || ''} />
          <ListItem leftIcon={<AlienIcon />} text={t('photo-printing-section.benefits.third') || ''} />
        </List>
      </div>
      <div className={ceilingsPhotoPrintingImageWrapper}>
        <CeilingsPhotoPrintingImage
          alt={t('photo-printing-section.title') || ''}
          onClick={handleImageClick(0)}
          src={`${CEILINGS_CDN_PATH}/photo-printing/1.jpg/1.jpg`}
        />
        <CeilingsPhotoPrintingImage
          alt={t('photo-printing-section.title') || ''}
          className={ceilingsPhotoPrintingBigImage}
          onClick={handleImageClick(1)}
          src={`${CEILINGS_CDN_PATH}/photo-printing/2.jpg/2.jpg`}
        />
        <CeilingsPhotoPrintingImage
          alt={t('photo-printing-section.title') || ''}
          onClick={handleImageClick(2)}
          src={`${CEILINGS_CDN_PATH}/photo-printing/3.jpg/3.jpg`}
        />
      </div>

      <GalleryModal
        clickedImageIndex={activeImageIndex}
        elementBlock={CEILINGS_ELEMENT_BLOCK}
        elementText={`${t('gallery-section.title')} / modal`}
        galleryList={galleryList}
        onClose={handleCloseModal}
        show={isModalVisible}
        isShowImageName={false}
        renderFooterButton={() => (
          <ButtonWithGAClickButton
            aria-label={t('design-panel.button') || ''}
            elementBlock={CEILINGS_ELEMENT_BLOCK}
            elementName={`${t('photo-printing-section.title')} / modal`}
            elementText={t('design-panel.button')}
            invert
            onClick={handleConsultationSignUpBtnClick}
            target="_blank"
            title={t('design-panel.button') || ''}
            variant="accent">
            {t('design-panel.button')}
          </ButtonWithGAClickButton>
        )}
      />
    </Section>
  );
};
